import * as React from "react";
import { create } from "zustand";
import { PaymentRequiredError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Promise-based confirms so screens can write
 *   if (await confirm({ title: "Delete Marta Reyes?", destructive: true })) ...
 * and
 *   await withCharges((accept) => users.create(accountId, doc, accept))
 * without wiring dialog state into every component. <ConfirmHost /> is
 * mounted once in main.tsx.
 */

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

const useConfirmStore = create<{ pending?: Pending; ask: (o: ConfirmOptions) => Promise<boolean>; settle: (ok: boolean) => void }>((set, get) => ({
  pending: undefined,
  ask: (o) =>
    new Promise<boolean>((resolve) => {
      get().pending?.resolve(false);
      set({ pending: { ...o, resolve } });
    }),
  settle: (ok) => {
    get().pending?.resolve(ok);
    set({ pending: undefined });
  },
}));

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().ask(options);
}

interface QuoteLine {
  category?: string;
  item?: string;
  name?: string;
  quantity?: number;
  rate?: number;
  total?: number;
  billable?: number;
}

/** Best-effort flattening of a 402 quote; crossbar's shape varies by version. */
function quoteLines(quote: unknown): QuoteLine[] {
  const q = quote as { invoices?: { items?: QuoteLine[] }[]; items?: QuoteLine[] } | undefined;
  if (Array.isArray(q?.items)) return q.items;
  if (Array.isArray(q?.invoices)) return q.invoices.flatMap((i) => i.items ?? []);
  return [];
}

/**
 * Run a billable request. On a 402 the quote is shown; if the operator
 * accepts, the request is re-run with accept_charges. Throws "cancelled"
 * when they decline. `note` is shown under the quote, for a caller that
 * will apply the same acceptance to a batch of writes.
 */
export async function withCharges<T>(run: (acceptCharges: boolean) => Promise<T>, opts: { note?: React.ReactNode } = {}): Promise<T> {
  try {
    return await run(false);
  } catch (err) {
    if (!(err instanceof PaymentRequiredError)) throw err;
    const lines = quoteLines(err.quote);
    const ok = await confirm({
      title: "This change will be billed",
      confirmLabel: "Accept charges",
      description: (
        <>
          {lines.length > 0 ? (
            <table className="w-full text-xs">
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-1 pr-2">{l.name ?? l.item ?? l.category}</td>
                    <td className="py-1 pr-2 text-right font-mono">{l.quantity ?? l.billable ?? ""}</td>
                    <td className="py-1 text-right font-mono">{l.total ?? l.rate ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <pre className="max-h-48 overflow-auto rounded bg-muted p-2 text-2xs">{JSON.stringify(err.quote, null, 2)}</pre>
          )}
          {opts.note && <p className="mt-2 text-xs">{opts.note}</p>}
        </>
      ),
    });
    if (!ok) throw new Error("cancelled", { cause: err });
    return run(true);
  }
}

export function ConfirmHost() {
  const pending = useConfirmStore((s) => s.pending);
  const settle = useConfirmStore((s) => s.settle);
  return (
    <Dialog open={Boolean(pending)} onOpenChange={(open) => !open && settle(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{pending?.title}</DialogTitle>
          {pending?.description && <DialogDescription asChild={typeof pending.description !== "string"}>{typeof pending.description === "string" ? pending.description : <div>{pending.description}</div>}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => settle(false)}>
            {pending?.cancelLabel ?? "Cancel"}
          </Button>
          <Button variant={pending?.destructive ? "destructive" : "default"} size="sm" onClick={() => settle(true)} autoFocus>
            {pending?.confirmLabel ?? "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
